import { FilterFilled, FilterTwoTone } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Descriptions, Divider, Flex, Grid, Input, Skeleton, Space, Table, Typography } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { debounce } from 'lodash';
import React, { Dispatch, Fragment, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getLeaveTicketsByPackage } from 'src/apis/hr_ticket/leaveTicket.api';
import { getLeavePackage } from 'src/apis/hr_ticket/leavePackage.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import { DepartmentTreeSelect } from 'src/components/v2/Select';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { ILeaveTicket } from 'src/types/hr_ticket/leaveTicket.type';
import { TableParams } from 'src/types/tableParams.type';
import { formatDate, getOrderStringV2 } from 'src/utils/utils';

import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import './LeavePackageDetail.scss';
const { Title, Text } = Typography;

const columns = function (
  filterDepartmentId: number | undefined,
  setFilterDepartmentId: Dispatch<SetStateAction<number | undefined>>,
  departmentFilterRef: React.RefObject<HTMLDivElement>,
  openDepartmentFilter: boolean,
  setOpenDepartmentFilter: Dispatch<SetStateAction<boolean>>
): ColumnsType<ILeaveTicket> {
  const columnTitle: ColumnsType<ILeaveTicket> = [
    {
      title: 'Mã phiếu',
      dataIndex: 'code',
      width: '20%',
      key: 'code',
      render: (_, record) => record?.code
    },
    {
      title: 'Người nghỉ phép',
      dataIndex: 'employee',
      key: 'employee',
      width: '30%',
      render: (_, record) => (
        <Text>
          {record.leaveTicketDetails?.[0]?.employee?.code} - {record.leaveTicketDetails?.[0]?.employee?.fullName}
        </Text>
      )
    },
    {
      title: 'Bộ phận',
      dataIndex: 'employee',
      key: 'employee',
      width: '25%',
      render: (_, record) => <Text>{record.leaveTicketDetails?.[0]?.employee?.department?.name}</Text>,
      filterDropdownOpen: openDepartmentFilter,
      filterDropdown: () => (
        <div
          style={{
            padding: 10
          }}
          ref={departmentFilterRef}
        >
          <DepartmentTreeSelect
            allowClear
            style={{ width: 250 }}
            value={filterDepartmentId}
            onChange={(value) => {
              setFilterDepartmentId(value);
              setOpenDepartmentFilter(false);
            }}
          />
        </div>
      ),
      filterIcon: filterDepartmentId ? (
        <FilterTwoTone style={{ fontSize: 14 }} onClick={() => setOpenDepartmentFilter(true)} />
      ) : (
        <FilterFilled style={{ fontSize: 12 }} onClick={() => setOpenDepartmentFilter(true)} />
      )
    },
    {
      title: 'Loại phép',
      dataIndex: 'leaveType',
      width: '25%',
      key: 'leaveType',
      render: (_, record) => record.leaveTicketDetails?.[0]?.leaveType?.name
    }
  ];

  return columnTitle;
};

export default function LeavePackageDetail() {
  const { setLeavePackage, setLoading } = useContext(HrTicketDetailContext);
  const { setInvalidKey } = useContext(TicketContext);
  const { ticketSelected } = useContext(AppContext);
  const { id } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<number | undefined>();
  const departmentFilterRef = React.useRef<HTMLDivElement>(null);
  const [openDepartmentFilter, setOpenDepartmentFilter] = useState<boolean>(false);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      pageSizeOptions: [10],
      showSizeChanger: false
    }
  });

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const { data, isFetching } = useQuery({
    queryKey: ['leavePackage', id],
    queryFn: () => getLeavePackage(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  useEffect(() => {
    const handleCloseFilter = (e: any) => {
      if (
        departmentFilterRef.current &&
        !departmentFilterRef.current.contains(e.target as Node) &&
        openDepartmentFilter
      ) {
        setOpenDepartmentFilter(false);
      }
    };

    document.addEventListener('click', handleCloseFilter);
    return () => document.removeEventListener('click', handleCloseFilter);
  }, [openDepartmentFilter]);

  const leavePackageDetails = useQuery({
    queryKey: [
      'leavePackageDetails',
      data?.data.id,
      filterDepartmentId,
      searchTerm,
      tableParams.pagination?.current ?? 1,
      tableParams.pagination?.pageSize ?? 10,
      getOrderStringV2('createdDate', 'desc')
    ],
    queryFn: () => {
      const params = {
        searchCondition: searchTerm,
        departmentId: filterDepartmentId,
        page: tableParams.pagination?.current ?? 1,
        pageSize: tableParams.pagination?.pageSize ?? 10,
        orderBy: getOrderStringV2('createdDate', 'desc')
      };

      return getLeaveTicketsByPackage(Number(id), params);
    },
    enabled: !!data?.data.id,
    staleTime: 60 * 1000,
    keepPreviousData: true
  });

  useEffect(() => {
    if (leavePackageDetails.data) {
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: leavePackageDetails.data.data.totalCount,
          current: Math.min(
            Number(prev.pagination?.current),
            leavePackageDetails.data.data.totalPages === 0 ? 1 : leavePackageDetails.data.data.totalPages
          )
        }
      }));
    }
  }, [leavePackageDetails.data]);

  useEffect(() => {
    if (data) {
      setLeavePackage(data);
      setLoading(isFetching);
      setInvalidKey(JSON.stringify(data?.data));
    }
  }, [data, isFetching, setLeavePackage, setLoading, setInvalidKey]);

  const handleTableChange = function (
    pagination: TablePaginationConfig,
    // filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ILeaveTicket> | SorterResult<ILeaveTicket>[]
  ) {
    setTableParams({
      field: (sorter as any).field,
      order: (sorter as any).order,
      pagination,
      // filters,
      ...sorter
    });
  };

  const debouncedSearchUser = useMemo(() => debounce((user) => setSearchTerm(user), 1000), []);

  return (
    <div className='ticket-detail-wrapper'>
      <BtnHrHandle type='leavePackage' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Flex justify='start'>
        <Title level={5}>Thông tin phiếu</Title>
      </Flex>
      {isFetching ? (
        <Skeleton active />
      ) : (
        <Fragment>
          <Descriptions
            className='ticket-detail-item-header'
            layout='horizontal'
            labelStyle={{ width: '135px' }}
            column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
          >
            <Descriptions.Item label='Mã phiếu'>{data?.data?.code}</Descriptions.Item>
            <Descriptions.Item label='Trạng thái'>
              <HRTicketStatusTag ticketStatus={data?.data.ticketStatus} />
            </Descriptions.Item>
            <Descriptions.Item label='Người tạo'>{data?.data?.createdBy?.fullName}</Descriptions.Item>
            <Descriptions.Item label='Bộ phận'>{data?.data?.createdBy?.department?.name}</Descriptions.Item>
            <Descriptions.Item label='Ngày tạo'>{formatDate(data?.data?.createdDate)}</Descriptions.Item>
            <Descriptions.Item label='Ngày nghỉ'>
              <Text strong>{formatDate(data?.data?.date)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Không áp dụng cho bộ phận' span={colspan}>
              <Text strong>
                {data?.data?.leavePackageExclusions
                  .filter(({ departmentId }) => !!departmentId)
                  .map(({ department }) => department?.name)
                  .join(', ')}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label='Không áp dụng cho nhân viên' span={colspan}>
              <Text strong>
                {data?.data?.leavePackageExclusions
                  .filter(({ employeeId }) => !!employeeId)
                  .map(({ employee }) => employee?.fullName)
                  .join(', ')}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={'Ghi chú'} span={colspan}>
              <Text italic>{data?.data?.note || ''}</Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ marginTop: 8, marginBottom: 8 }} />
          <Table
            className='overtime-confirm-table'
            columns={columns(
              filterDepartmentId,
              setFilterDepartmentId,
              departmentFilterRef,
              openDepartmentFilter,
              setOpenDepartmentFilter
            )}
            title={() => {
              return (
                <Flex align='center' justify='space-between'>
                  <Title level={5}>Danh sách ngày nghỉ</Title>
                  <Space>
                    <span>Tìm kiếm: </span>
                    <Input
                      style={{ width: 200 }}
                      onChange={(event) => debouncedSearchUser(event.target.value)}
                      placeholder='Nhập mã phiếu'
                      allowClear
                    />
                  </Space>
                </Flex>
              );
            }}
            loading={leavePackageDetails.isFetching}
            rowKey={(record) => record.id}
            dataSource={leavePackageDetails.data?.data.results}
            pagination={tableParams.pagination}
            onChange={handleTableChange}
            bordered={false}
            size='middle'
          />
        </Fragment>
      )}
    </div>
  );
}
